import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { getItem, removeItem, setItem } from "../cookie/Cookie";
import { persistor } from "../store";

interface AppContextType {
  authToken: boolean | null;
  signIn: (params: SignInParams) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  loggingOut: boolean;
  setLoggingOut: (loggingOut: boolean) => void;
}
interface SignInParams {
  key: string;
  value: string;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppContextProvider = ({ children }: { children: ReactNode }) => {
  const [authToken, setAuthToken] = useState<boolean | null>(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const signIn = async ({ key, value }: SignInParams) => {
    await setItem(key, value);
    const token = await getItem("token");
    setAuthToken(!!token);
  };

  const signOut = async () => {
    localStorage.removeItem("persist:root");
    persistor.flush();
    persistor.purge();
    localStorage.clear(); // optional: clear any manual storage
    sessionStorage.clear();

    await removeItem("token");
    setAuthToken(false);
  };

  const checkAuth = async () => {
    const token = await getItem("token");
    setAuthToken(!!token);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AppContext.Provider
      value={{
        authToken,
        signIn,
        signOut,
        checkAuth,
        loggingOut,
        setLoggingOut,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }
  return context;
};
